import { useState } from 'react';
import {
  canShowConversionRate,
  formatRate,
  readLocalReportFile,
  reportStatus,
  type TrialConversionReport,
} from '../lib/trial-conversion-report.ts';

function StatCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-lg bg-white p-5 shadow">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">{value}</p>
      {detail ? <p className="mt-1 text-sm text-gray-500">{detail}</p> : null}
    </div>
  );
}

export default function TrialConversionPage() {
  const [report, setReport] = useState<TrialConversionReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function openReport(file: File | undefined): Promise<void> {
    if (!file) return;
    const loaded = await readLocalReportFile(file);
    if (!loaded.ok) {
      setError(loaded.error);
      return;
    }
    setError(null);
    setReport(loaded.report);
  }

  const showRate = report != null && canShowConversionRate(report);
  const status = report == null ? null : reportStatus(report);

  return (
    <div className="px-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Trial conversion</h2>
          <p className="mt-1 text-sm text-gray-500">
            Opens a report file in this browser. It does not query the backend or RevenueCat.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white">
          {report ? 'Open another report' : 'Open report JSON'}
          <input
            type="file"
            accept="application/json,.json"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              void openReport(file);
              event.target.value = '';
            }}
          />
        </label>
      </div>

      {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}

      {report && status ? (
        <>
          <p className="mt-6 text-sm text-gray-600">
            Generated: {report.generatedAt}. Data through: {report.observationCutoff}. Status: {status}.
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <StatCard label="Mature trials" value={String(report.headline.matureTrials)} />
            <StatCard label="Converted" value={showRate ? String(report.headline.converted) : '—'} />
            <StatCard label="Rate" value={formatRate(report.headline.rate, showRate)} />
          </div>
          <div className="mt-8 overflow-hidden rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Cohort', 'Platform', 'Trials', 'During', 'After', 'Refunds', 'Unknown'].map((heading) => (
                    <th
                      key={heading}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {report.cohorts.map((row) => (
                  <tr key={`${row.weekStart}-${row.platform}`}>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.weekStart}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.platform}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.confirmedTrials}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.duringTrialPurchases}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.afterTrialPurchases}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.refunds}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.unknown}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {report.trialStartsByPlacement.length > 0 ? (
            <div className="mt-8 overflow-hidden rounded-lg bg-white p-5 shadow">
              <h3 className="mb-4 text-lg font-semibold">Trial start locations</h3>
              <table className="min-w-full text-left text-sm">
                <thead><tr><th>Location</th><th>Platform</th><th>Confirmed starts</th></tr></thead>
                <tbody>
                  {report.trialStartsByPlacement.map((row) => (
                    <tr key={`${row.platform}-${row.placement}`}>
                      <td className="py-2">{row.placement}</td><td>{row.platform}</td><td>{row.trialStarts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-sm text-gray-500">Older trials without a recorded location appear as unknown. Failed grants are excluded.</p>
            </div>
          ) : null}
          <ul className="mt-6 list-disc space-y-1 pl-5 text-sm text-gray-600">
            {report.limitations.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-8 text-sm text-gray-500">No report loaded.</p>
      )}
    </div>
  );
}
